import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useProduct } from '../hooks/useProduct';
import { useLanguage } from '../hooks/useLanguage';
import Product from './app/Product/Product';
import { getItemsByProduct } from '../data/item';
import { useEffect, useRef } from 'react';

export default function ProductPanel() {
  const { activeProductId, setActiveProductId } = useProduct();
  const { language } = useLanguage();
  const desktopScrollRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);

  const items = getItemsByProduct(activeProductId);

  // Reset scroll to top when product changes
  useEffect(() => {
    if (activeProductId) {
      if (desktopScrollRef.current) {
        desktopScrollRef.current.scrollTop = 0;
      }
      if (mobileScrollRef.current) {
        mobileScrollRef.current.scrollTop = 0;
      }
    }
  }, [activeProductId]);
  return (
    <>
      {/* Desktop Product Panel - Next to navigation, slides from bottom */}
      <AnimatePresence>
        {activeProductId && (
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
              opacity: { duration: 0.2 },
            }}
            className="hidden lg:flex lg:flex-col fixed left-[8.3vw] top-[2.5vh] h-fit max-h-[75vh] min-h-[32vh] w-[38vw] bg-transparent z-40 shadow-2xl overflow-hidden rounded-2xl"
          >
            {/* Header */}
            <div className="bg-red-600 px-[0.9vh] py-[0.5vh] flex items-center justify-between shrink-0">
              <h2 className="text-white text-[1.05vw] font-extrabold tracking-wider ml-[0.5vh]">
                {language === 'en' ? 'OUR PRODUCTS' : 'NOS PRODUITS'}
              </h2>
              <button
                onClick={() => setActiveProductId(null)}
                className="text-white hover:text-gray-200 transition-colors mr-[0.3vh]"
              >
                <X className="w-[1.4vw] h-[1.4vw]" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div
              ref={desktopScrollRef}
              className="flex-1 overflow-y-auto overflow-x-hidden bg-red-600/10 backdrop-blur-sm [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-red-100 [&::-webkit-scrollbar-thumb]:bg-red-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-red-700"
            >
              <Product items={items} />
            </div>

            {/* Footer */}
            <div className="bg-red-600 px-[0.9vh] py-[0.5vh] text-white text-[0.82vw] font-bold shrink-0">
              <span className="ml-[0.5vh]">
                {items.length} {language === 'en' ? 'products' : 'produits'}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Product Panel - Slides from bottom, above navigation */}
      <AnimatePresence>
        {activeProductId && (
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
              opacity: { duration: 0.2 },
            }}
            className="lg:hidden flex flex-col fixed inset-x-2 bottom-20 top-16 bg-transparent backdrop-blur-sm z-40 overflow-hidden rounded-2xl shadow-2xl"
          >
            {/* Header */}
            <div className="bg-red-600 px-3 py-2 flex items-center justify-between shadow-lg shrink-0">
              <h2 className="text-white text-[3.5vw] font-extrabold tracking-wider ml-1">
                {language === 'en' ? 'OUR PRODUCTS' : 'NOS PRODUITS'}
              </h2>
              <button
                onClick={() => setActiveProductId(null)}
                className="text-white hover:text-gray-200 transition-colors mr-0.5"
              >
                <X className="w-[4.5vw] h-[4.5vw]" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div
              ref={mobileScrollRef}
              className="flex-1 overflow-y-auto overflow-x-hidden bg-red-600/10 backdrop-blur-sm [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-red-100 [&::-webkit-scrollbar-thumb]:bg-red-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-red-700"
            >
              <Product items={items} />
            </div>

            {/* Footer */}
            <div className="bg-red-600 px-3 py-2 text-white text-[2.5vw] font-bold shrink-0">
              <span className="ml-1">
                {items.length} {language === 'en' ? 'products' : 'produits'}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
